"use client";

import { Button } from "@/components/ui/button";
import type { WidgetParams } from "@/components/widgets";
import { WidgetContent } from "@/components/widgets/SideDrawerWidgetContent";
import type { OpenWidgetParams } from "@/context/RightNavContext";
import * as checkout from "@imtbl/checkout-sdk";
import { ArrowLeft } from "lucide-react";
import { useMemo } from "react";
import { FundWidgetContent } from "./FundWidgetContent";

interface CurrentWidgetProps {
  widgetConfig: OpenWidgetParams | null;
  widgetParams: WidgetParams | null;
}

function CurrentWidget({ widgetConfig, widgetParams }: CurrentWidgetProps) {
  if (!widgetConfig) return null;

  switch (widgetConfig.tab) {
    case "Swap":
    case "Bridge":
    case "Buy":
      return <FundWidgetContent />;
    case "Send":
    case "Receive":
      return <WidgetContent widgetParams={widgetParams} />;
    default:
      return null;
  }
}

interface WidgetTabContentProps {
  widgetConfig: OpenWidgetParams | null;
  closeWidgetTab: () => void;
  toggleCollapse: () => void;
}

export function WidgetTabContent({
  widgetConfig,
  closeWidgetTab,
  toggleCollapse,
}: WidgetTabContentProps) {
  const widgetParams = useMemo((): WidgetParams | null => {
    if (!widgetConfig) return null;

    switch (widgetConfig.tab) {
      case "Send":
        return {
          type: "CommerceWidgetFlow",
          params: { flow: checkout.CommerceFlowType.TRANSFER },
          config: {
            TRANSFER: {
              showHeader: false,
              customCoinAmountTitle: "",
            },
            themeOverrides: {
              drawerBackground: "transparent",
            },
          },
        };
      case "Receive":
        return {
          type: "CEXDeposit",
          params: { defaultFrom: "BINANCE" },
        };
      default:
        return null;
    }
  }, [widgetConfig]);

  if (!widgetConfig) return null;

  const title =
    widgetConfig.tab === "Swap" ||
    widgetConfig.tab === "Bridge" ||
    widgetConfig.tab === "Buy"
      ? "Fund"
      : widgetConfig.tab;

  return (
    <div
      className="h-full flex flex-col relative"
      style={{
        backdropFilter: "blur(50px)",
        WebkitBackdropFilter: "blur(50px)",
      }}
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/20" />

      {/* Content Layer */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Tab Header with Back Button */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3 flex-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={closeWidgetTab}
              className="text-white/80 hover:bg-white/10 hover:text-white h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">{title}</h2>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div
          className="flex-1 min-h-0 w-full max-w-full overflow-hidden
            [&_[id^='checkout-container-']]:!w-full
            [&_[id^='checkout-container-']]:!h-full
            [&_[id^='checkout-container-']]:!max-h-full
            [&_[id^='checkout-container-']]:!overflow-auto
            [&_[data-testid='simpleLayout']]:!w-full
            [&_[data-testid='simpleLayout']]:!h-full
            [&_[data-testid='simpleLayout']]:!max-h-full
            [&_[data-testid='simpleLayout']]:!overflow-auto
            [&_[data-testid='container']]:!bg-transparent
            [&_[data-testid='container']]:!h-full
            [&_[data-testid='container']]:!max-h-full
            [&_[data-testid='container']]:!overflow-auto
            [&_[data-testid='header-navigation-container']]:!bg-transparent
            [&_[data-testid='header-navigation-container']]:!backdrop-blur-none
            [&_img[alt='blurry bg texture']]:!hidden
            [&_img[alt='background texture']]:!hidden
            [&_img[src*='add-tokens-bg-texture']]:!hidden
            [&_div[style*='position: absolute'][style*='height: 100%'][style*='width: 100%']]:!hidden
            [&_div:has(img[data-testid='CloudImage'])]:!h-full"
        >
          <CurrentWidget
            widgetConfig={widgetConfig}
            widgetParams={widgetParams}
          />
        </div>
      </div>
    </div>
  );
}
